import { PERMISSION_GROUPS } from './permissionGroups';
import { canOpenPage } from './pagePermissions';

/**
 * Which modules a person can open. Their role decides first, then the business's plan and setup:
 *
 * 1. Role: full-access roles (super and system admin, or an `admin` permission) open every
 *    module; any other role opens a module when it holds at least one of that module's
 *    permissions (the module tags in utils/permissionGroups.js; shared permissions open none).
 * 2. Plan: for a business One Manager manages, only the modules its plan includes (GET /hub-plan
 *    `features`, the same rule the API enforces).
 * 3. The business's own setup and subscription, when the account carries them.
 *
 * Someone with more than one module chooses on the module selection page; someone with one goes
 * straight to it.
 */

export const FULL_ACCESS_ROLES = ['super_admin', 'system_admin'];
const PLAN_MODULES = ['retail', 'wholesale'];

const keysOf = (module) =>
  PERMISSION_GROUPS.filter((group) => group.module === module).flatMap((group) =>
    group.permissions.map((permission) => permission.key)
  );
const MODULE_KEYS = { retail: keysOf('retail'), wholesale: keysOf('wholesale') };

// When an account's role permissions are unknown (older sessions), its role name decides.
const ROLE_MODULES = {
  retail_admin: ['retail'],
  branch_admin: ['retail'],
  branch_staff: ['retail'],
  wholesale_admin: ['wholesale'],
  wholesale_staff: ['wholesale'],
  admin: ['retail'],
  staff: ['retail']
};

/** The modules the person's role opens. */
export function modulesForRole(user) {
  if (!user) return [];
  const permissions = user.permissions;
  if (FULL_ACCESS_ROLES.includes(user.role) || permissions?.admin === true) return [...PLAN_MODULES];
  if (!permissions || typeof permissions !== 'object') return ROLE_MODULES[user.role] || ['retail'];
  return PLAN_MODULES.filter((module) => MODULE_KEYS[module].some((key) => permissions[key] === true));
}

/** The modules the person can open in this business. `plan` is GET /hub-plan's data, or null. */
export function availableModules(user, plan) {
  if (!user) return [];
  let modules = modulesForRole(user);

  if (plan?.managed && Array.isArray(plan.features)) {
    modules = modules.filter((module) => plan.features.includes(module));
  }

  const subscribed = user.modules && Object.keys(user.modules).length > 0 ? user.modules : null;
  if (subscribed) modules = modules.filter((module) => subscribed[module]);

  const active = Array.isArray(user.moduleConfig?.activeModules) && user.moduleConfig.activeModules.length > 0
    ? user.moduleConfig.activeModules
    : null;
  if (active) modules = modules.filter((module) => active.includes(module));

  // Accounting is a module only for businesses subscribed to it separately
  if (subscribed?.accounting && (!active || active.includes('accounting')) && canOpenPage(user, '/accounting') !== false) {
    modules.push('accounting');
  }
  return modules;
}
